import React from 'react';
import { tokens } from '../../design/tokens';

/**
 * StatusPill - Reusable status badge component
 * Moved outside BrandTable to prevent re-creation on every render
 */
const StatusPill = ({ status, variant = 'default' }) => {
  const variantClasses = {
    success: 'bg-green-100 text-green-800 border-green-200',
    primary: 'bg-purple-100 text-purple-800 border-purple-200',
    warning: 'bg-orange-100 text-orange-800 border-orange-200',
    danger: 'bg-red-100 text-red-800 border-red-200',
    default: 'bg-gray-100 text-gray-800 border-gray-200'
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${variantClasses[variant]}`}>
      {status}
    </span>
  );
};

/**
 * BrandTable - Branded table component with rounded corners and token-based styling
 * Supports density modes and status pills
 */
const BrandTable = ({ 
  columns = [],
  data = [],
  density = 'comfortable', // comfortable or compact
  emptyState = 'No data available',
  className = ''
}) => {
  // Density classes
  const densityClasses = {
    comfortable: 'py-3 px-4',
    compact: 'py-2 px-3'
  };

  const cellPadding = densityClasses[density] || densityClasses.comfortable;

  // Render cell content
  const renderCell = (column, row) => {
    const value = row[column.key];
    
    // Handle status pill rendering
    if (column.type === 'status' && column.statusMap) {
      const variant = column.statusMap[value] || 'default';
      return <StatusPill status={value} variant={variant} />;
    }
    
    // Handle currency
    if (column.type === 'currency') {
      return <span className="font-medium">₹{parseFloat(value).toLocaleString('en-IN')}</span>;
    }
    
    // Handle custom renderer
    if (column.render) {
      return column.render(value, row);
    }
    
    return value;
  };

  if (!data || data.length === 0) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <div className="p-12 text-center text-gray-500">
          {emptyState}
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm ${className}`}>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          {/* Header */}
          <thead className="bg-gray-50/60 sticky top-0">
            <tr>
              {columns.map((column, idx) => (
                <th
                  key={idx}
                  className={`${cellPadding} text-left text-xs font-medium uppercase tracking-wider text-gray-500`}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          
          {/* Body */}
          <tbody className="bg-white divide-y divide-gray-100">
            {data.map((row, rowIdx) => (
              <tr key={rowIdx} className="hover:bg-gray-50 transition-colors">
                {columns.map((column, colIdx) => (
                  <td
                    key={colIdx}
                    className={`${cellPadding} text-sm text-gray-900 whitespace-nowrap`}
                  >
                    {renderCell(column, row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BrandTable;
